import {
  isDynamoWrapper,
  isDynamoList,
  isDynamoItem,
  isDynBound,
  isGridEl,
  isComponent,
  isRichBlock,
  isTypographicBlock,
  isAllowedTypographicTag,
} from '@packages/systems/core/utils/DomTypeUtils';
import type {Context} from '../types';
import type {JSONNode} from '@packages/shared/types/JSONNode';

// Note: type 3 text_nodes are also valid and have undefined tag name
// These are node tags that cannot be direct children of divs or lis unless the div/li is typographic.
// Note: We're deliberately not using SharedConfig.VALID_TYPOGRAPHIC_CONTENT_NODES here because that const may change
// in the future and currently contains `a` tags which would invalidate the reasoning in this patch.
const TYPOGRAPHIC_BLOCK_INDICATOR_CHILDREN = ['strong', 'em', 'br', 'span'];

const isValidNodeType = (tagName: string, data: Record<any, any>) => {
  // Only allow specific tags to become typographic
  // ['div', 'li', 'blockquote']
  if (!isAllowedTypographicTag(tagName)) {
    return false;
  }

  // If already a typographic block (already has (data.text = true) it doesn't need this patch.
  if (isTypographicBlock(tagName, data)) {
    return false;
  }

  // Rich text nodes are not typographic nodes, although they contain descendants that look similar
  if (isRichBlock(tagName, data)) {
    return false;
  }

  // Don't consider grid elements
  if (isGridEl(data)) {
    return false;
  }

  // Don't consider DynList instance nodes, DynList item templates, or bound dynamic nodes
  if (
    isDynamoWrapper(data) ||
    isDynamoList(data) ||
    isDynamoItem(data) ||
    isDynBound(data)
  ) {
    return false;
  }

  // Component wrappers should only ever contain real nodes, not typographic text
  if (isComponent(data)) {
    return false;
  }

  // The node has a valid type and could be a typographic block
  return true;
};

const mustBeTypographicBlock = (
  jsonNode: JSONNode,
  nodeMap: Map<string, JSONNode>
) => {
  // Bail if it has no content
  if (!jsonNode.children || !jsonNode.children.length) {
    return false;
  }

  // Now it gets tricky, the key here is differentiating between something like a div -> anchor -> type 3 node
  // where the div is either a typographic block or just a div wrapper with an anchor inside. ie: when you double click
  // in the designer, does it start editing the div's content including the anchor, or the anchor's content.
  // It's actually not possible to differentiate in all cases now that the div has content.
  // (it would have been empty when originally setting data.text = true) So now we need to look at the content.

  // Ultimately we only need to make sure that all type 3 text_nodes and things like strong/em/span/br that can only be
  // children of typographic nodes are nested by some kind of typographic node.
  // In the case of div -> anchor -> type 3, you can still double click on the anchor to edit the type 3 with the RTE
  // if the div doesn't have data.text. So it's not necessary to repair that div and better to lean toward not repairing it.

  // Check if any of this node's children require it to be a typographic node.
  const indicatorChild = jsonNode.children.find((childId) => {
    // Note: node.text is a type 3 text_node that doesn't have a tag. Not the same as node.data.text which
    // is a normal element like a div that's been made typographic.
    const child = nodeMap.get(childId);
    if (!child) return false;
    const {tag, text} = child;
    const isType3Node = !tag && text;
    return (
      isType3Node || (tag && TYPOGRAPHIC_BLOCK_INDICATOR_CHILDREN.includes(tag))
    );
  });

  if (indicatorChild) {
    return true;
  }

  // Since none of the children require it to be a typogographic node we can err on the side of not patching this node
  // leaving it as a wrapper div

  return false;
};

export default (context: Context) => {
  const {
    node: {jsonNode},
    tree: {nodeMap},
  } = context;
  const {data = {}} = jsonNode;
  const tagName = jsonNode.tag;

  // First check the tagName and data for elements that we know are not typogrphic blocks
  const mightBeTypographicBlockWithoutDataTextProp =
    tagName && isValidNodeType(tagName, data);
  // Then check the children to see if any require this node to ba a typographic block.
  const needsMigration =
    mightBeTypographicBlockWithoutDataTextProp &&
    mustBeTypographicBlock(jsonNode, nodeMap);

  return {
    needed: Boolean(needsMigration),
    logData: {
      msg: 'Static typographic block missing its data.text prop',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      data.text = true;
      // If jsonNode had no data prop then data was defaulted
      // to an empty object, make sure data is attached to the jsonNode
      jsonNode.data = data;
    },
  };
};
